import { objectLookup } from "../..";
import { agentSessionEventFromService } from "../../models/aiAgents";
import { AGENT_CONFIGS } from "./agents.config";
import { AgentSessionEventAttachments } from "./sessions.events.attachments";
import { AgentDataParseError } from "./errors";
export class AgentSessionEvents {
    constructor(sdk, agent) {
        this.sdk = sdk;
        this.$inferDataType = {};
        const dataConfig = objectLookup(AGENT_CONFIGS, `${agent}.event.data`);
        this.dataFromService =
            dataConfig?.fromService ||
                ((data, _sdk) => data);
        this.onParseError = dataConfig?.onParseError ?? "throw";
        this.attachments = new AgentSessionEventAttachments(sdk, agent);
    }
    async parseData(data) {
        try {
            const parsedData = await this.dataFromService(data, this.sdk);
            return parsedData;
        }
        catch (err) {
            // If custom handler, pass original error directly
            if (typeof this.onParseError === "function") {
                this.onParseError(err, this.sdk);
                return undefined;
            }
            // For throw/skip modes, wrap in AgentDataParseError
            const parseError = new AgentDataParseError(err instanceof Error ? err.message : "Unknown parse error", err);
            console.error("Error parsing agent session event data:", parseError);
            switch (this.onParseError) {
                case "skip":
                    return undefined;
                case "throw":
                default:
                    throw parseError;
            }
        }
    }
    /**
     * Converts a raw service event (legacy REST, legacy SSE, or ADK) to the canonical
     * {@link AgentSessionEvent}, then parses its `data` field for this agent.
     *
     * Data parsing is skipped for partial SSE events, since the backend only sends
     * structured `data` on non-partial (final) events.
     */
    async fromService(serviceEvent) {
        const event = agentSessionEventFromService(serviceEvent);
        const data = event.partial || typeof serviceEvent.data === "undefined"
            ? undefined
            : await this.parseData(serviceEvent.data);
        return {
            ...event,
            data
        };
    }
    latestWithMessage(session, message, { role } = {}) {
        const events = session.events || [];
        for (let i = events.length - 1; i >= 0; i--) {
            const event = events[i];
            const text = event?.content?.parts
                ?.map((part) => part.text || "")
                .join("")
                .trim();
            // `text` can legitimately be "" (e.g. attachment-only sends some agents pad with a single
            // space to satisfy a non-empty-message requirement); only `undefined` means "no content to
            // compare" (missing/nullish `content`), so that's the only case that should skip matching.
            if (text !== undefined && text === message.trim() && (!role || event.role === role)) {
                return event;
            }
        }
        return undefined;
    }
    isLatestUserEvent(session, eventId) {
        return this.isLatestEvent(session, eventId, { role: "user" });
    }
    isLatestAgentEvent(session, eventId) {
        return this.isLatestEvent(session, eventId, { role: "model" });
    }
    isLatestEvent(session, eventId, { role } = {}) {
        const events = session.events || [];
        for (let i = events.length - 1; i >= 0; i--) {
            const event = events[i];
            if (event.id === eventId) {
                return true;
            }
            if (event.role === role) {
                return false;
            }
        }
        return false;
    }
}
//# sourceMappingURL=sessions.events.js.map